import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DailyBusinessSummaryData from './daily-business-summary-data';
import i18n from '../../../i18n';
import { BusinessSummaryData, DailyBusinessData } from '../../../models/fixed/data-analysis';
import TableComponent, { HeadCell } from '../../../component/table/table.component';
import { MobxObjectsManager, useManualRemove } from '../../mobx-objs/mobx-manager';
import MainPageState from '../../main/main-page-states';
import dataAnalysisStore from '../data-analysis-state';
import moment from 'moment';
import { ErrorMessageBox } from '../data-analysis.page';
import { formatDateWithWeekday } from '../../../constants/utils';
import { formatTotalTax, formatTotalWithoutTax } from './shared';
import { Box, Card, Typography } from '@mui/material';
import PeriodDateFilter from '../../../component/period-date-filter.component';
import { formatEUR } from '../../../constants/tool.fn';
import { DataExportIconButton } from '../../../component/button/data-export-icon-button.component';
import GlobalNotification from '../../../component/common/snackbar';
import './scss/daily-business-summary.style.scss';
import ColumnVisibilityPopover, { ColumnVisibilityOption } from '../../../component/table/column-visibility-popover.component';
import {
    getInitialBusinessSummaryColumnIds,
} from './column-visibility';

// 汇总数组下标映射，避免魔法数字分散在渲染中
const SUMMARY_INDEX_MAP = {
    // 总人数对应的下标
    totalCustomers: 1,
    // 开桌数对应的下标
    openTables: 2,
    // 实收总额对应的下标
    totalAmount: 3,
} as const;

// 固定列 id：这三列需常驻且左侧固定
const REQUIRED_COLUMN_IDS = ['order_date', 'total_customers', 'open_tables'] as const;

// 合计数组中固定列的下标映射
const SUMMATION_INDEX_MAP: Record<string, number> = {
    // 日期列
    order_date: 0,
    // 人数列
    total_customers: 1,
    // 开桌数列
    open_tables: 2,
    // 实收总额列
    total_actual_amount: 3,
    // 堂食实收总额列
    eat_in_actual_amount_total: 4,
    // 外卖实收总额列
    take_out_actual_amount_total: 5,
    // 堂食开桌数量列
    eat_in_order_total: 6,
    // 外卖订单数量列
    take_out_order_total: 7,
    // 折扣列
    total_discount: 8,
    // 应税总额列
    total_tax_amount: 9,
    // 未税总额列
    total_without_tax_amount: 10,
    // 服务费列
    total_service_amount: 11,
    // 服务费应税总额列
    total_service_tax: 12,
    // 小费列
    total_tip_amount: 13,
    // 小费应税总额列
    total_tip_tax: 14,
};

/**
 * 列配置项：用于弹窗展示与表格列过滤
 */
type BusinessSummaryColumnOption = ColumnVisibilityOption & {
    // 列唯一 id
    id: string;
    // 列标题
    label: string;
    // 表格 headCell
    headCell: HeadCell;
    // 是否必须显示（不可取消）
    required: boolean;
}


// mobx数据管理
export const businessSummaryDataStore = MobxObjectsManager.get(DailyBusinessSummaryData)
/**
 * 日营业汇总页面
 */
const DailyBusinessSummaryPage = observer(() => {

    useTranslation();
    /** 从主页面状态取当前餐厅，不依赖「当前营业状态」页是否打开 */
    const mainPageData = MobxObjectsManager.get(MainPageState);
    const stringId = mainPageData.currentRestaurant?.string_id || '';
    // 当前时间段筛选
    const startDate = moment(dataAnalysisStore.startDate).format('YYYY-MM-DD HH:mm');
    const endDate = moment(dataAnalysisStore.endDate).format('YYYY-MM-DD HH:mm');
    const data = businessSummaryDataStore.data;
    // 汇总区展示：请求加载中显示占位，避免日期切换期间出现旧数据
    const summaryTotalAmount = businessSummaryDataStore.loading
        ? null
        : Number(businessSummaryDataStore.summationArray[SUMMARY_INDEX_MAP.totalAmount] ?? 0);
    const summaryTotalCustomers = businessSummaryDataStore.loading
        ? null
        : Number(businessSummaryDataStore.summationArray[SUMMARY_INDEX_MAP.totalCustomers] ?? 0);
    const summaryOpenTables = businessSummaryDataStore.loading
        ? null
        : Number(businessSummaryDataStore.summationArray[SUMMARY_INDEX_MAP.openTables] ?? 0);

    useEffect(() => {
        businessSummaryDataStore.fetchData(stringId, startDate, endDate);
        return () => {
            // 卸载时清空排序值
            businessSummaryDataStore.setEatInArrSort({}, 'businessSummary')
        }
    }, [stringId, startDate, endDate])

    // 组件卸载时移除HistoryOrderDataState
    useManualRemove(DailyBusinessSummaryData);

    /**
     * 当前排序状态
     * @param key
     * @returns
     */
    const getSortStatus = (key: string, id: keyof DailyBusinessData) => {
        const eatInArrSort = businessSummaryDataStore.sortKeyValue[id];
        if (key === eatInArrSort?.key) {
            return eatInArrSort.value === 'ASC' ? 2 : 1
        }
        return 0
    }

    // 每日营业汇总静态表头（按 label 长度设置 minWidth，避免过挤并支持横向滚动）
    const staticHeadCellListBusinessData: HeadCell[] = [
        {
            id: 'order_date',
            label: i18n.t('business_hour_page_date'),
            minWidth: '140px',
            sortStatus: getSortStatus('order_date', 'businessSummary'),
            renderFunction: (item) => formatDateWithWeekday(item.order_date),
            sticky: 'left',
        },
        {
            id: 'total_customers',
            label: i18n.t('immediate_number_people'),
            minWidth: '90px',
            sortStatus: getSortStatus('total_customers', 'businessSummary'),
            renderFunction: (item) => {
                return <>{item?.total_customers}</>
            },
            sticky: 'left'
        },
        {
            id: 'open_tables',
            label: i18n.t('daily_business_summary_page_table_opening'),
            minWidth: '90px',
            // 除序号外均可排序
            sortStatus: getSortStatus('open_tables', 'businessSummary'),
            sticky: 'left'
        },
        {
            id: 'total_actual_amount',
            label: i18n.t('business_analysis_page_text_actual_amount_total'),
            minWidth: '120px',
            sortStatus: getSortStatus('total_actual_amount', 'businessSummary'),
            renderFunction: (item) => {
                return <>{Number(item?.total_actual_amount ?? 0).toFixed(2)}</>
            }
        },
        {
            id: 'eat_in_actual_amount_total',
            label: i18n.t('business_analysis_page_text_hall_amount_total'),
            minWidth: '120px',
            sortStatus: getSortStatus('eat_in_actual_amount_total', 'businessSummary'),
            renderFunction: (item) => {
                return <>{Number(item?.eat_in_actual_amount_total ?? 0).toFixed(2)}</>
            }
        },
        {
            id: 'take_out_actual_amount_total',
            label: i18n.t('business_analysis_page_text_takeout_amount_total'),
            minWidth: '120px',
            sortStatus: getSortStatus('take_out_actual_amount_total', 'businessSummary'),
            renderFunction: (item) => {
                return <>{Number(item?.take_out_actual_amount_total ?? 0).toFixed(2)}</>
            }
        },
        {
            id: 'eat_in_order_total',
            label: i18n.t('business_analysis_page_text_dine_in_table_nums'),
            minWidth: '120px',
            sortStatus: getSortStatus('eat_in_order_total', 'businessSummary'),
            renderFunction: (item) => {
                return <>{Number(item?.eat_in_order_total ?? 0)}</>
            }
        },
        {
            id: 'take_out_order_total',
            label: i18n.t('business_analysis_page_text_takeout_total'),
            minWidth: '120px',
            sortStatus: getSortStatus('take_out_order_total', 'businessSummary'),
            renderFunction: (item) => {
                return <>{Number(item?.take_out_order_total ?? 0)}</>
            }
        },
        {
            id: 'total_discount',
            label: i18n.t('daily_business_summary_page_discount'),
            minWidth: '90px',
            sortStatus: getSortStatus('total_discount', 'businessSummary'),
            renderFunction: (item) => {
                return <>{Number(item?.total_discount ?? 0).toFixed(2)}</>
            }
        },
        {
            id: 'total_tax_amount',
            label: i18n.t('daily_business_summary_page_should_pay_taxes_total_amount'),
            minWidth: '160px',
            sortStatus: getSortStatus('total_tax_amount', 'businessSummary'),
            renderFunction: (item) => (<>{formatTotalTax(item).toFixed(2)}</>)
        },
        {
            id: 'total_without_tax_amount',
            label: i18n.t('daily_business_summary_page_gross_untaxed'),
            minWidth: '120px',
            sortStatus: getSortStatus('total_without_tax_amount', 'businessSummary'),
            renderFunction: (item) => formatTotalWithoutTax(item).toFixed(2),
        },
        {
            id: 'total_service_amount',
            label: i18n.t('business_analysis_page_text_service_charges'),
            minWidth: '100px',
            sortStatus: getSortStatus('total_service_amount', 'businessSummary'),
            renderFunction: (item) => (<>{Number(item.total_service_amount ?? 0).toFixed(2)}</>)
        },
        {
            id: 'total_service_tax',
            label: i18n.t('manager_text_total_taxable_service_fee'),
            minWidth: '140px',
            sortStatus: getSortStatus('total_service_tax', 'businessSummary'),
            renderFunction: (item) => (<>{Number(item.total_service_tax ?? 0).toFixed(2)}</>)
        },
        {
            id: 'total_tip_amount',
            label: i18n.t('business_analysis_page_text_tip'),
            minWidth: '90px',
            sortStatus: getSortStatus('total_tip_amount', 'businessSummary'),
            renderFunction: (item) => (<>{Number(item.total_tip_amount ?? 0).toFixed(2)}</>)
        },
        {
            id: 'total_tip_tax',
            label: i18n.t('manager_text_total_taxable_tip'),
            minWidth: '120px',
            sortStatus: getSortStatus('total_tip_tax', 'businessSummary'),
            renderFunction: (item) => (<>{Number(item.total_tip_tax ?? 0).toFixed(2)}</>)
        },
    ];

    // 动态支付方式列：表头与单元格由 paymentMethodColumns 生成
    // 动态支付方式列：与静态列一样支持表头排序（按该支付方式金额）
    const dynamicPaymentHeadCellList: HeadCell[] = useMemo(() => businessSummaryDataStore.paymentMethodColumns.map((pc) => ({
        id: `payment_${pc.tender_media_id}`,
        label: pc.tender_media_name,
        minWidth: '90px',
        sortStatus: getSortStatus(`payment_${pc.tender_media_id}`, 'businessSummary'),
        renderFunction: (item: BusinessSummaryData) => {
            const pm = (item.payment_methods ?? []).find(p => p.tender_media_id === pc.tender_media_id);
            return <>{Number(pm?.amount ?? 0).toFixed(2)}</>;
        },
    })), [
        businessSummaryDataStore.paymentMethodColumns,
        // 排序切换时需重算表头 sortStatus，否则动态列排序箭头不更新
        businessSummaryDataStore.sortKeyValue.businessSummary,
    ]);

    // 全量列配置（静态 + 动态）
    const allColumnOptions: BusinessSummaryColumnOption[] = useMemo(() => {
        const staticColumns: BusinessSummaryColumnOption[] = staticHeadCellListBusinessData.map((headCell) => ({
            id: headCell.id,
            label: headCell.label,
            headCell,
            required: REQUIRED_COLUMN_IDS.includes(headCell.id as typeof REQUIRED_COLUMN_IDS[number]),
        }));
        const dynamicColumns: BusinessSummaryColumnOption[] = dynamicPaymentHeadCellList.map((headCell) => ({
            id: headCell.id,
            label: headCell.label,
            headCell,
            required: false,
        }));
        return [...staticColumns, ...dynamicColumns];
    }, [dynamicPaymentHeadCellList, staticHeadCellListBusinessData]);
    // 当前可选列 id 快照（用于控制同步 effect 触发）
    const availableColumnIdKey = allColumnOptions.map((column) => column.id).join('|');

    // 已选中的列 id（新增加的订单数量列默认隐藏）
    const [selectedColumnIds, setSelectedColumnIds] = useState<string[]>([]);

    /**
     * 同步可选列变化：
     * - 首次进入默认显示既有列，新增订单数量列默认隐藏
     * - 动态列变更时保留已选，移除已不存在的列，并自动包含必选列
     */
    useEffect(() => {
        setSelectedColumnIds((prev) => {
            if (prev.length === 0) {
                return getInitialBusinessSummaryColumnIds(allColumnOptions.map((column) => column.id));
            }
            const availableIds = new Set(allColumnOptions.map((column) => column.id));
            const next = prev.filter((id) => availableIds.has(id));
            REQUIRED_COLUMN_IDS.forEach((requiredId) => {
                if (!next.includes(requiredId)) {
                    next.unshift(requiredId);
                }
            });
            return Array.from(new Set(next));
        });
    }, [availableColumnIdKey]);

    // 当前显示的表头列（按原始顺序过滤）
    const visibleHeadCellListBusinessData: HeadCell[] = useMemo(() => allColumnOptions
        .filter((column) => selectedColumnIds.includes(column.id))
        .map((column) => column.headCell), [allColumnOptions, selectedColumnIds]);

    // 当前显示列对应的合计数组（与 visibleHeadCellListBusinessData 保持同序）
    const visibleSummationArray: (string | number)[] = useMemo(() => visibleHeadCellListBusinessData
        .map((headCell) => {
            if (SUMMATION_INDEX_MAP[headCell.id] !== undefined) {
                return businessSummaryDataStore.summationArray[SUMMATION_INDEX_MAP[headCell.id]] ?? '';
            }
            if (headCell.id.startsWith('payment_')) {
                const tenderMediaId = Number(headCell.id.replace('payment_', ''));
                const paymentIndex = businessSummaryDataStore.paymentMethodColumns.findIndex((column) => column.tender_media_id === tenderMediaId);
                if (paymentIndex >= 0) {
                    return businessSummaryDataStore.summationArray[15 + paymentIndex] ?? '';
                }
            }
            return '';
        }), [businessSummaryDataStore.paymentMethodColumns, businessSummaryDataStore.summationArray, visibleHeadCellListBusinessData]);

    // 可切换显示的数据列；必显列不在浮层中展示
    const optionalColumnOptions = useMemo(() => allColumnOptions.filter((column) => !column.required), [allColumnOptions]);

    // 浮层实际使用的选中列，仅包含可切换的列 id
    const optionalSelectedColumnIds = useMemo(() => selectedColumnIds.filter((id) =>
        optionalColumnOptions.some((column) => column.id === id)
    ), [optionalColumnOptions, selectedColumnIds]);

    /**
     * 切换列显示状态（必选列不可取消）
     * @param column - 当前操作列
     */
    const handleToggleColumn = (column: BusinessSummaryColumnOption) => {
        if (column.required) {
            return;
        }
        setSelectedColumnIds((prev) => {
            if (prev.includes(column.id)) {
                return prev.filter((id) => id !== column.id);
            }
            return [...prev, column.id];
        });
    };

    /**
     * 根据公共浮层组件返回的选项 id 切换列显示状态。
     * @param option - 浮层当前点击的列选项
     */
    const handleToggleColumnOption = (option: ColumnVisibilityOption) => {
        const column = optionalColumnOptions.find((item) => item.id === option.id);
        if (!column) {
            return;
        }
        handleToggleColumn(column);
    };

    /**
     * 排序
     * @param headCell
     */
    const handleSort = (headCell: HeadCell, key: keyof DailyBusinessData) => {
        let sortStatus: 'ASC' | 'DESC' | '' = ''
        switch (headCell.sortStatus) {
            case 0:
                sortStatus = 'DESC'
                break;
            case 1:
                sortStatus = 'ASC'
                break;
            case 2:
                sortStatus = ''
                break;
            default:
                sortStatus = ''
                break;
        }
        businessSummaryDataStore.setEatInArrSort({
            value: sortStatus ? sortStatus : undefined,
            key: sortStatus ? headCell.id : undefined
        }, key)
        businessSummaryDataStore.setDataSort(key as keyof DailyBusinessData)
    }

    /** 与导出同源：所选日期无汇总行则提示、不导空表 */
    const preCheckExport = useCallback((): boolean => {
        if (!data.businessSummary?.length) {
            GlobalNotification.warning(i18n.t('general_text_no_data'));
            return false;
        }
        return true;
    }, [data.businessSummary?.length]);

    const handleExportClick = useCallback(() => {
        businessSummaryDataStore.downloadExcel();
    }, []);

    return (
        businessSummaryDataStore.errorMessage ?
            <ErrorMessageBox errorMessage={businessSummaryDataStore.errorMessage} /> :
            <Box className='daily-business-summary-page'>
                <Card className='daily-business-summary-card'>
                    <Box className='daily-business-summary-top-bar'>
                        <Typography className='daily-business-summary-title'>
                            {i18n.t('daily_business_summary_page_text_daily_business_summary')}
                        </Typography>
                        <Box className='daily-business-summary-top-actions'>
                            <DataExportIconButton
                                title={i18n.t('tunnel_management_page_export')}
                                disabled={businessSummaryDataStore.loading}
                                preCheck={preCheckExport}
                                onExport={handleExportClick}
                            />
                            <PeriodDateFilter
                                startDate={dataAnalysisStore.startDate}
                                endDate={dataAnalysisStore.endDate}
                                pickerValue={dataAnalysisStore.pickerValue}
                                onDateTimeChange={dataAnalysisStore.handleDateTime}
                            />
                        </Box>
                    </Box>
                    <Box className='daily-business-summary-overview'>
                        <Box className='daily-business-summary-overview-row'>
                            <Typography className='daily-business-summary-overview-text'>
                                {`${i18n.t('daily_business_summary_page_total_turnover')}：${summaryTotalAmount === null ? '--' : formatEUR(summaryTotalAmount)} | `}
                                {`${i18n.t('daily_business_summary_page_total_customers')}：${summaryTotalCustomers ?? '--'}${i18n.t('daily_business_summary_page_people_unit')} | `}
                                {`${i18n.t('daily_business_summary_page_total_tables')}：${summaryOpenTables ?? '--'}${i18n.t('daily_business_summary_page_table_unit')} |`}
                            </Typography>
                            <ColumnVisibilityPopover
                                title={i18n.t('daily_business_summary_page_select_visible_columns')}
                                options={optionalColumnOptions}
                                selectedIds={optionalSelectedColumnIds}
                                onToggle={handleToggleColumnOption}
                                id='daily-business-summary-column-popover'
                                renderTrigger={({ onClick, ariaProps }) => (
                                    <button
                                        type='button'
                                        className='daily-business-summary-column-btn'
                                        onClick={onClick}
                                        {...ariaProps}
                                    >
                                        {i18n.t('daily_business_summary_page_select_visible_columns')}
                                    </button>
                                )}
                            />
                        </Box>
                    </Box>
                    <Box className='daily-business-summary-table-wrapper'>
                        <TableComponent
                            extraClass='daily-business-summary-table'
                            // 分页
                            isPagination
                            allCount={data.businessSummary.length}
                            // 列表项
                            headCellList={visibleHeadCellListBusinessData}
                            // 列表数据
                            list={data.businessSummary}
                            // 是否显示复选框 0 不显示 1显示  2 显示但是不显示删除提示框
                            showChecked={0}
                            // 是否显示加载框 0不开启
                            isTableLoading={businessSummaryDataStore.loading}
                            // 排序方法
                            handleSort={(value) => {
                                handleSort(value, 'businessSummary')
                            }}
                            // 启用表格数据合计功能
                            isActivateSummation
                            // 横向滚动时固定序号列（表头字号较大，序号列宽与 sticky 偏移对齐）
                            stickySerialColumn
                            stickyLeadingWidthsPx={{ serial: 88 }}
                            // 合计后的每列累加数据
                            summationArray={visibleSummationArray}
                        />
                    </Box>
                </Card>
            </Box>
    )

});

export default DailyBusinessSummaryPage;